import { format } from 'date-fns'

// BRIEF-E5d: every time on a customer page is in the business's own time
// zone, never the browser's and never UTC. /auth/me says which zone
// (backend/app/desk/business_zone.py); authStore hands it here. Auckland is
// only the fallback before that answer, and for staff, who have no business.
export const FALLBACK_TIME_ZONE = 'Pacific/Auckland'

let currentZone = FALLBACK_TIME_ZONE

/** The zone the customer pages show times in. */
export function businessTimeZone(): string {
  return currentZone
}

/** Set by authStore from the signed-in principal. A missing or unknown zone
 *  falls back: a bad name must not throw on every page that shows a time. */
export function setBusinessTimeZone(zone: string | null | undefined): void {
  if (!zone) {
    currentZone = FALLBACK_TIME_ZONE
    return
  }
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone })
    currentZone = zone
  } catch {
    currentZone = FALLBACK_TIME_ZONE
  }
}

/** The wall-clock time in the business's zone, as a Date whose local fields
 *  carry it (so date-fns can format it). */
export function inBusinessZone(d: Date, timeZone: string = currentZone): Date {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  }).formatToParts(d)
  const n = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0)
  return new Date(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'), n('second'))
}

/** A time formatted in the business's zone, or null when there is none. */
export function businessTime(
  value: string | Date | null | undefined,
  pattern = 'EEE d MMM, HH:mm',
): string | null {
  if (!value) return null
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return format(inBusinessZone(d), pattern)
}

/** A date with no time ("2026-11-02"), named as it stands, never shifted by a
 *  zone: the same day in London and Auckland. */
export function plainDay(iso: string, pattern = 'EEE d MMM'): string | null {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return null
  return format(new Date(y, m - 1, d), pattern)
}

/** An hour of the day given in UTC (0-23), as that hour in the business's
 *  zone today: "5pm". */
export function businessHourFromUtc(utcHour: number, now: Date = new Date()): string {
  const whole = Math.floor(utcHour)
  const minutes = Math.round((utcHour - whole) * 60)
  const at = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), ((whole % 24) + 24) % 24, minutes),
  )
  const local = inBusinessZone(at)
  return format(local, local.getMinutes() === 0 ? 'haaa' : 'h:mmaaa')
}
